import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiSun, FiMoon } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext.jsx';
import { useTheme } from '../../context/ThemeContext.jsx';
import PersonalNotificationBell from '../notifications/PersonalNotificationBell.jsx';
import Avatar from '../common/Avatar.jsx';

const Header = ({ toggleSidebar, pageTitle, pageSubtitle, pageActions, pageInfo }) => {
  const { currentUser, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const quickCreateBtn = (
    <Link
      to="/projects/create"
      className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
    >
      <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      <span className="hidden sm:inline">New Project</span>
    </Link>
  );

  const toolbarActions = (
    <>
      {pageActions ? (
        Array.isArray(pageActions) ? (
          <div className="flex items-center space-x-1">
            {pageActions.map((action, index) => (
              <div key={index} className="flex-shrink-0">{action}</div>
            ))}
          </div>
        ) : (
          <div className="flex-shrink-0">{pageActions}</div>
        )
      ) : (
        quickCreateBtn
      )}
      <div className="flex-shrink-0">
        <PersonalNotificationBell />
      </div>
      <button
        type="button"
        onClick={toggleDarkMode}
        className="flex-shrink-0 p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
      </button>
      <div className="relative flex-shrink-0 ml-1 sm:ml-0" ref={dropdownRef}>
        <button
          type="button"
          className="bg-white dark:bg-gray-800 rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
          id="user-menu"
          aria-expanded={userMenuOpen}
          aria-haspopup="true"
          onClick={() => setUserMenuOpen(!userMenuOpen)}
        >
          <span className="sr-only">Open user menu</span>
          <Avatar
            user={currentUser}
            size="sm"
            className="h-8 w-8 shrink-0 cursor-default text-sm shadow-md hover:opacity-100 sm:h-10 sm:w-10 sm:text-lg"
          />
        </button>
        {userMenuOpen && (
          <div
            className="origin-top-right absolute right-0 mt-2 w-56 sm:w-64 rounded-lg shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none z-50"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="user-menu"
          >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{currentUser?.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{currentUser?.email}</p>
            </div>
            <Link
              to="/profile"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setUserMenuOpen(false)}
            >
              <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Your Profile
            </Link>
            <Link
              to="/settings"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setUserMenuOpen(false)}
            >
              <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </Link>
            <div className="border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                role="menuitem"
              >
                <svg className="w-4 h-4 mr-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const hamburgerBtn = (
    <button
      type="button"
      className="md:hidden inline-flex shrink-0 items-center justify-center self-center rounded-lg p-2 -ml-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
      onClick={toggleSidebar}
    >
      <span className="sr-only">Open sidebar</span>
      <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
  );

  const titleBlock =
    pageTitle ? (
      <div className="min-w-0">
        <h1 className="truncate text-base font-semibold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-lg md:text-xl">
          {pageTitle}
        </h1>
        {pageSubtitle ? (
          <p className="mt-0.5 text-[11px] leading-snug text-gray-600 dark:text-gray-400 line-clamp-2 sm:mt-1 sm:text-xs sm:leading-snug md:text-sm md:leading-snug md:line-clamp-none">
            {pageSubtitle}
          </p>
        ) : null}
        {pageInfo ? (
          <div className="mt-1 text-[11px] leading-snug text-gray-600 dark:text-gray-400 sm:text-xs sm:leading-relaxed md:mt-2 md:max-w-3xl md:text-sm">
            {pageInfo}
          </div>
        ) : null}
      </div>
    ) : (
      <h1 className="truncate text-base font-semibold leading-tight text-gray-900 dark:text-white sm:text-xl">
        Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}!
      </h1>
    );

  /* Mobile: single row [menu][title · subtitle][actions] — no full-width wrap so title stays visible. md+: same strip with more air. */
  return (
    <header className="z-40 border-b border-gray-200 bg-white shadow-sm transition-colors duration-200 dark:border-gray-700 dark:bg-gray-800 pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-0">
      <div className="px-3 sm:px-4 lg:px-8">
        <div className="flex w-full flex-nowrap items-center gap-2 py-2 sm:gap-3 sm:py-2.5 md:min-h-[4rem] md:gap-x-5 md:py-2 lg:items-center">
          {hamburgerBtn}
          <div className="min-w-0 flex-1">
            {titleBlock}
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2 lg:gap-3">
            {toolbarActions}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
